import { inject } from '@angular/core';
import { LocalStorageFacade } from '@application/localstorage';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  INACTIVATION_VIDEO_NOTICE_LAST_DAY,
  INACTIVATION_VIDEO_NOTICE_STORAGE_KEY,
} from '../entities/inactivation-video.model';

interface TInactivationVideoNoticeState {
  /**
   * The same marks, for this tab only. `LocalStorageFacade` writes nothing when the
   * storage is blocked; without this the video would reopen on every navigation.
   */
  seenInTab: Record<string, string>;
}

/**
 * Who already saw the inactivation video today, per person (`TenantId:PersonId`): the
 * reception computer is shared, and the video has to reach everyone who logs in on it.
 * Days are `yyyy-MM-dd` in the account's zone, decided by the caller.
 */
export const InactivationVideoNoticeStore = signalStore(
  { providedIn: 'root' },
  withState<TInactivationVideoNoticeState>({ seenInTab: {} }),
  withMethods((store, localStorage = inject(LocalStorageFacade)) => {
    /** Garbage or a blocked storage reads as "nobody saw it"; the next write fixes it. */
    const readStored = (): Record<string, string> => {
      try {
        const seen = JSON.parse(localStorage.getItem(INACTIVATION_VIDEO_NOTICE_STORAGE_KEY) ?? '{}');
        return seen && typeof seen === 'object' && !Array.isArray(seen) ? seen : {};
      } catch {
        return {};
      }
    };

    const writeStored = (seen: Record<string, string>): void => {
      try {
        localStorage.setItem(INACTIVATION_VIDEO_NOTICE_STORAGE_KEY, JSON.stringify(seen));
      } catch {
        // Quota full: the mark of this tab still holds.
      }
    };

    return {
      shouldShow(owner: string, today: string): boolean {
        return (
          today <= INACTIVATION_VIDEO_NOTICE_LAST_DAY &&
          store.seenInTab()[owner] !== today &&
          readStored()[owner] !== today
        );
      },

      markSeen(owner: string, today: string): void {
        patchState(store, { seenInTab: { ...store.seenInTab(), [owner]: today } });
        writeStored({ ...readStored(), [owner]: today });
      },

      /** The video left the screen without being watched (another modal opened on top). */
      unmarkSeen(owner: string): void {
        const seenInTab = { ...store.seenInTab() };
        const stored = readStored();
        delete seenInTab[owner];
        delete stored[owner];
        patchState(store, { seenInTab });
        writeStored(stored);
      },
    };
  })
);
